import type { components } from '../../../types/openapi';
import type { Schema } from '../../../types/typeHelper';

export const TICKET_VIEWS = {
  FORM: 'form',
  DETAILS: 'details',
} as const;

/**
 * We configured the Elastic Beanstalk to have a maximun upload limit of: 20MB.
 * In: gcs-api/src/.platform/nginx/conf.d/client_max_body_size.conf
 * Using a bit less than that to accomodate for metadata overhead.
 */
export const MAX_TICKET_ATTACHMENT_SIZE = 19.996 * 1024 * 1024;

export const DELETE_NOTIFICATION_AFTER_SECONDS = 0;

export type TicketStati = components['schemas']['EtsStatusEnum'];

export enum TicketDashboardTabNames {
  SENT = 'sent',
  IN_PROGRESS = 'in_progress',
  IN_INQUIRY = 'in_inquiry',
  DECLINED = 'declined',
  WONT_DO = 'wont_do',
  DONE = 'done',
  NOT_LISTED = 'not_listed',
  UNKNOWN = 'unknown',
}

export type TicketStatusBadges = Partial<
  Record<
    TicketStati | TicketDashboardTabNames.NOT_LISTED | 'rating',
    {
      title: string;
      iconColor: string;
      backgroundColor: string;
      foregroundColor: string;
    }
  >
>;

type TicketDashboardTabs = {
  [key in TicketDashboardTabNames]: {
    title: string;
    status: TicketStati;
  };
};

export const TICKET_DASHBOARD_TABS: TicketDashboardTabs = {
  [TicketDashboardTabNames.SENT]: {
    title: 'Gesendet',
    status: 'sent',
  },
  [TicketDashboardTabNames.IN_PROGRESS]: {
    title: 'In Bearbeitung',
    status: 'in_progress',
  },
  [TicketDashboardTabNames.IN_INQUIRY]: {
    title: 'Lieferantenanfrage',
    status: 'in_inquiry',
  },
  [TicketDashboardTabNames.DECLINED]: {
    title: 'Rückmeldung erforderlich',
    status: 'declined',
  },
  [TicketDashboardTabNames.UNKNOWN]: {
    title: 'Artikel unbekannt',
    status: 'unknown',
  },
  [TicketDashboardTabNames.WONT_DO]: {
    title: 'Ersatzteil nicht verfügbar',
    status: 'wont_do',
  },
  [TicketDashboardTabNames.DONE]: {
    title: 'Erledigt',
    status: 'done',
  },
  [TicketDashboardTabNames.NOT_LISTED]: {
    title: 'Nicht gelistet',
    status: 'done',
  },
};

export const TICKET_STATUS_BADGES: TicketStatusBadges = {
  sent: {
    title: TICKET_DASHBOARD_TABS[TicketDashboardTabNames.SENT].title,
    iconColor: 'fill-info-9',
    backgroundColor: 'bg-info-4',
    foregroundColor: 'text-info-11',
  },
  in_progress: {
    title: TICKET_DASHBOARD_TABS[TicketDashboardTabNames.IN_PROGRESS].title,
    iconColor: 'fill-info-9',
    backgroundColor: 'bg-info-4',
    foregroundColor: 'text-info-11',
  },
  in_inquiry: {
    title: TICKET_DASHBOARD_TABS[TicketDashboardTabNames.IN_INQUIRY].title,
    iconColor: 'fill-info-9',
    backgroundColor: 'bg-info-4',
    foregroundColor: 'text-info-11',
  },
  declined: {
    title: TICKET_DASHBOARD_TABS[TicketDashboardTabNames.DECLINED].title,
    iconColor: 'fill-warning-9',
    backgroundColor: 'bg-warning-4',
    foregroundColor: 'text-warning-11',
  },
  wont_do: {
    title: TICKET_DASHBOARD_TABS[TicketDashboardTabNames.WONT_DO].title,
    iconColor: 'fill-error-9',
    backgroundColor: 'bg-error-4',
    foregroundColor: 'text-error-11',
  },
  done: {
    title: TICKET_DASHBOARD_TABS[TicketDashboardTabNames.DONE].title,
    iconColor: 'fill-success-9',
    backgroundColor: 'bg-success-4',
    foregroundColor: 'text-success-11',
  },
  not_listed: {
    title: TICKET_DASHBOARD_TABS[TicketDashboardTabNames.NOT_LISTED].title,
    iconColor: 'fill-warning-9',
    backgroundColor: 'bg-warning-4',
    foregroundColor: 'text-warning-11',
  },
  rating: {
    title: 'Anfrage bewerten',
    iconColor: 'fill-info-9',
    backgroundColor: 'bg-info-alpha-3',
    foregroundColor: 'text-info-alpha-11',
  },
  unknown: {
    title: TICKET_DASHBOARD_TABS[TicketDashboardTabNames.UNKNOWN].title,
    iconColor: 'fill-error-9',
    backgroundColor: 'bg-error-4',
    foregroundColor: 'text-error-11',
  },
} as const;

type TicketInputChannels = {
  [key in Schema<'EtsInputChannelEnum'>]: {
    key: Schema<'EtsInputChannelEnum'>;
    label: string;
    hidden?: boolean;
  };
};

export const TICKET_INPUT_CHANNELS: TicketInputChannels = {
  email: {
    key: 'email',
    label: 'E-Mail',
  },
  phone: {
    key: 'phone',
    label: 'Telefon',
  },
  chat: {
    key: 'chat',
    label: 'BEEM',
  },
  craftsman: {
    key: 'craftsman',
    label: 'Fachhandwerker',
    /**
     * This value is automatically set in the craftsman app
     * and shouldn't be selected by an agent.
     */
    hidden: true,
  },
};

export const defaultAllowedFileTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/heic', 'image/webp'];

export const TICKET_SUBMISSION_SOURCE = {
  MOBILE: 'MOBILE',
  DESKTOP: 'DESKTOP',
};
